import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { FileCheck, Shield, Play, CheckCircle, ChevronDown, ChevronRight, Globe, Server, AlertTriangle, Info, X, TrendingUp, TrendingDown, Minus, BarChart3, Download, FileText, ClipboardList, Building2, CreditCard, Lock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar } from 'recharts';
import { useTheme } from '@/contexts/ThemeContext';

const COMPLIANCE_STANDARDS = [
  // Global Standards
  { value: 'owasp', label: 'OWASP Top 10', desc: 'Web Application Security', region: 'Global' },
  { value: 'pci-dss', label: 'PCI-DSS', desc: 'Payment Card Industry', region: 'Global' },
  { value: 'gdpr', label: 'GDPR', desc: 'EU Data Protection', region: 'Global' },
  { value: 'hipaa', label: 'HIPAA', desc: 'Healthcare Data', region: 'Global' },
  { value: 'soc2', label: 'SOC 2', desc: 'Service Organizations', region: 'Global' },
  { value: 'nist-csf', label: 'NIST CSF', desc: 'Cybersecurity Framework', region: 'Global' },
  { value: 'iso-27001', label: 'ISO 27001', desc: 'Information Security Management', region: 'Global' },
  // Indian Compliance Standards
  { value: 'dpdp', label: 'DPDP Act 2023', desc: 'Digital Personal Data Protection', region: 'India' },
  { value: 'rbi-csf', label: 'RBI CSF', desc: 'Reserve Bank of India Framework', region: 'India' },
  { value: 'sebi-csf', label: 'SEBI CSF', desc: 'Securities Board Framework', region: 'India' },
  { value: 'cert-in', label: 'CERT-In', desc: 'Indian CERT Directives', region: 'India' },
];

export default function Compliance() {
  const { isDarkMode } = useTheme();
  const [checks, setChecks] = useState([]);
  const [domains, setDomains] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStandard, setSelectedStandard] = useState('owasp');
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [running, setRunning] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [activeTab, setActiveTab] = useState('checks');
  const [trendAnalytics, setTrendAnalytics] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [selectedTrendRegion, setSelectedTrendRegion] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === 'trends') {
      fetchTrendAnalytics();
    }
  }, [activeTab, selectedTrendRegion]);

  const fetchData = async () => {
    try {
      const [checksRes, domainsRes, assetsRes] = await Promise.all([
        api.get('/compliance'),
        api.get('/easm/domains').catch(() => ({ data: [] })),
        api.get('/assets')
      ]);
      setChecks(checksRes.data?.checks || checksRes.data || []);
      setDomains(domainsRes.data?.domains || domainsRes.data || []);
      setAssets(assetsRes.data?.assets || assetsRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch compliance data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrendAnalytics = async () => {
    setTrendLoading(true);
    try {
      const params = new URLSearchParams({ days: '90' });
      if (selectedTrendRegion !== 'all') {
        params.append('region', selectedTrendRegion);
      }
      const response = await api.get(`/compliance/trends/analytics?${params.toString()}`);
      setTrendAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to fetch trend analytics');
    } finally {
      setTrendLoading(false);
    }
  };

  const handleRunCheck = async () => {
    setRunning(true);
    try {
      const params = new URLSearchParams({ standard: selectedStandard });
      if (selectedDomain !== 'all') {
        params.append('domain', selectedDomain);
      }
      if (selectedAssets.length > 0) {
        params.append('assets', selectedAssets.join(','));
      }
      
      await api.post(`/compliance/check?${params.toString()}`);
      toast.success(`${COMPLIANCE_STANDARDS.find(s => s.value === selectedStandard)?.label} compliance check completed`);
      fetchData();
    } catch (error) {
      toast.error('Failed to run compliance check');
    } finally {
      setRunning(false);
    }
  };

  const openCheckDetail = (check) => {
    setSelectedCheck(check);
    setDetailOpen(true);
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-400';
    if (score >= 70) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreBg = (score) => {
    if (score >= 90) return 'bg-emerald-500/20 border-emerald-500/30';
    if (score >= 70) return 'bg-yellow-500/20 border-yellow-500/30';
    return 'bg-red-500/20 border-red-500/30';
  };

  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev => 
      prev.includes(assetId) 
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-indigo-400 font-rajdhani text-xl">Loading compliance data...</div>
      </div>
    );
  }

  return (
    <div data-testid="compliance-container" className="space-y-6 scroll-container">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`font-rajdhani font-bold text-4xl ${isDarkMode ? 'text-white text-shadow-neon' : 'text-slate-900'}`}>
            Compliance Center
          </h1>
          <p className={`font-inter mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Industry standard compliance monitoring including Indian regulations</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid w-full max-w-2xl grid-cols-5 ${isDarkMode ? 'bg-zinc-900/50' : 'bg-slate-100'}`}>
          <TabsTrigger value="checks" data-testid="compliance-checks-tab" className="data-[state=active]:bg-indigo-500">
            <FileCheck className="w-4 h-4 mr-2" />
            Checks
          </TabsTrigger>
          <TabsTrigger value="exceptions" data-testid="compliance-exceptions-tab" className="data-[state=active]:bg-indigo-500">
            <Shield className="w-4 h-4 mr-2" />
            Exceptions
          </TabsTrigger>
          <TabsTrigger value="controls" data-testid="compensating-controls-tab" className="data-[state=active]:bg-indigo-500">
            <Lock className="w-4 h-4 mr-2" />
            Controls
          </TabsTrigger>
          <TabsTrigger value="certin" data-testid="certin-tab" className="data-[state=active]:bg-indigo-500">
            <AlertCircle className="w-4 h-4 mr-2" />
            CERT-In
          </TabsTrigger>
          <TabsTrigger value="exports" data-testid="compliance-exports-tab" className="data-[state=active]:bg-indigo-500">
            <Download className="w-4 h-4 mr-2" />
            Exports
          </TabsTrigger>
        </TabsList>

        <TabsContent value="checks" className="mt-6">
          <div className={`rounded-2xl p-6 border ${isDarkMode ? 'glass' : 'bg-white border-slate-200 shadow-sm'}`}>
        {/* Compliance Configuration */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
          {/* Standard Selection */}
          <div>
            <Label className={`text-xs mb-2 block ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Compliance Standard</Label>
            <Select value={selectedStandard} onValueChange={setSelectedStandard}>
              <SelectTrigger data-testid="compliance-standard-select" className={`w-full ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={`max-h-80 ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
                <div className={`px-2 py-1 text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Global Standards</div>
                {COMPLIANCE_STANDARDS.filter(s => s.region === 'Global').map(std => (
                  <SelectItem key={std.value} value={std.value}>
                    <div>
                      <span>{std.label}</span>
                      <span className={`ml-2 text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>({std.desc})</span>
                    </div>
                  </SelectItem>
                ))}
                <div className={`px-2 py-1 text-xs text-orange-400 mt-2 border-t pt-2 ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
                  Indian Compliance
                </div>
                {COMPLIANCE_STANDARDS.filter(s => s.region === 'India').map(std => (
                  <SelectItem key={std.value} value={std.value}>
                    <div>
                      <span>{std.label}</span>
                      <span className={`ml-2 text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>({std.desc})</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Domain Selection */}
          <div>
            <Label className={`text-xs mb-2 block ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Filter by Domain</Label>
            <Select value={selectedDomain} onValueChange={setSelectedDomain}>
              <SelectTrigger className={`w-full ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                <SelectValue placeholder="All Domains" />
              </SelectTrigger>
              <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}>
                <SelectItem value="all">All Domains</SelectItem>
                {domains.map(domain => (
                  <SelectItem key={domain.id} value={domain.domain}>
                    <div className="flex items-center gap-2">
                      <Globe className="w-3 h-3 text-emerald-400" />
                      {domain.domain}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Asset Selection */}
          <div>
            <Label className={`text-xs mb-2 block ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Filter by Assets ({selectedAssets.length} selected)</Label>
            <Select>
              <SelectTrigger className={`w-full ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                <SelectValue placeholder="Select Assets" />
              </SelectTrigger>
              <SelectContent className={`max-h-60 ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
                {assets.length === 0 ? (
                  <div className={`p-3 text-center text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>No assets available</div>
                ) : (
                  assets.slice(0, 20).map(asset => (
                    <div 
                      key={asset.id} 
                      className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}
                      onClick={() => toggleAssetSelection(asset.id)}
                    >
                      <Checkbox
                        checked={selectedAssets.includes(asset.id)}
                        onCheckedChange={() => toggleAssetSelection(asset.id)}
                        className={isDarkMode ? 'border-white/20' : 'border-slate-300'}
                      />
                      <Server className="w-3 h-3 text-indigo-400" />
                      <span className="text-sm">{asset.name}</span>
                    </div>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          
          {/* Run Button */}
          <div className="flex items-end">
            <Button
              data-testid="run-compliance-check-button"
              onClick={handleRunCheck}
              disabled={running}
              className="w-full bg-indigo-600 hover:bg-indigo-500 font-rajdhani shadow-neon-indigo"
            >
              <Play className="w-4 h-4 mr-2" />
              {running ? 'Running...' : 'Run Compliance Check'}
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className={`p-5 rounded-xl border ${isDarkMode ? 'glass-hover border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-indigo-600/20 border border-indigo-500/30' : 'bg-indigo-100 border border-indigo-200'}`}>
                <FileCheck className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
              </div>
              <div>
                <p className={`text-sm font-inter ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Total Checks</p>
                <p className={`font-rajdhani text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{checks.length}</p>
              </div>
            </div>
          </div>

          <div className={`p-5 rounded-xl border ${isDarkMode ? 'glass-hover border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-emerald-600/20 border border-emerald-500/30' : 'bg-emerald-100 border border-emerald-200'}`}>
                <CheckCircle className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
              </div>
              <div>
                <p className={`text-sm font-inter ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Passed Controls</p>
                <p className={`font-rajdhani text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {checks.reduce((sum, c) => sum + c.passed_controls, 0)}
                </p>
              </div>
            </div>
          </div>

          <div className={`p-5 rounded-xl border ${isDarkMode ? 'glass-hover border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-orange-600/20 border border-orange-500/30' : 'bg-orange-100 border border-orange-200'}`}>
                <Shield className={`w-5 h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
              </div>
              <div>
                <p className={`text-sm font-inter ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Avg Score</p>
                <p className={`font-rajdhani text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {checks.length > 0
                    ? (checks.reduce((sum, c) => sum + c.score, 0) / checks.length).toFixed(1)
                    : '0.0'}%
                </p>
              </div>
            </div>
          </div>

          <div className={`p-5 rounded-xl border ${isDarkMode ? 'glass-hover border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-red-600/20 border border-red-500/30' : 'bg-red-100 border border-red-200'}`}>
                <AlertTriangle className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
              </div>
              <div>
                <p className={`text-sm font-inter ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Failed Controls</p>
                <p className={`font-rajdhani text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {checks.reduce((sum, c) => sum + (c.total_controls - c.passed_controls), 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Compliance Checks List */}
        <div className="space-y-4">
          <h3 className={`font-rajdhani font-semibold text-lg ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Compliance Check Results</h3>
          
          {checks.length === 0 ? (
            <div className="text-center py-12">
              <FileCheck className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
              <p className={`font-inter ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>No compliance checks found</p>
              <p className={`text-sm font-inter mt-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>Run your first compliance check above</p>
            </div>
          ) : (
            checks.map((check) => (
              <div
                key={check.id}
                data-testid={`compliance-check-${check.id}`}
                onClick={() => openCheckDetail(check)}
                className={`p-5 rounded-xl border transition-all cursor-pointer ${isDarkMode ? 'glass-hover border-white/10 hover:border-indigo-500/30' : 'bg-white border-slate-200 hover:border-indigo-300 hover:shadow-md'}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center border ${getScoreBg(check.score)}`}>
                      <span className={`font-rajdhani text-lg font-bold ${getScoreColor(check.score)}`}>
                        {check.score.toFixed(0)}%
                      </span>
                    </div>
                    <div>
                      <h4 className={`font-rajdhani font-semibold text-xl uppercase ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {check.standard}
                      </h4>
                      <p className={`text-sm font-inter ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        Checked on {new Date(check.checked_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right flex items-center gap-4">
                    <div>
                      <p className={`text-sm font-inter mb-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Controls</p>
                      <p className={`font-rajdhani text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        <span className="text-emerald-500">{check.passed_controls}</span> / {check.total_controls}
                      </p>
                    </div>
                    <ChevronRight className={`w-5 h-5 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                  </div>
                </div>

                <Progress value={check.score} className="h-2 mb-3" />

                {check.findings && check.findings.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {check.findings.slice(0, 3).map((finding, idx) => (
                      <span
                        key={idx}
                        className={`px-2 py-1 rounded text-xs ${
                          finding.status === 'passed'
                            ? 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/30'
                            : 'bg-red-500/20 text-red-500 border border-red-500/30'
                        }`}
                      >
                        {finding.control?.substring(0, 30)}...
                      </span>
                    ))}
                    {check.findings.length > 3 && (
                      <span className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-slate-100 text-slate-500'}`}>
                        +{check.findings.length - 3} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
        </TabsContent>

        {/* Exception Approval Workflows Tab */}
        <TabsContent value="exceptions" className="mt-6">
          <ExceptionWorkflowPanel isDarkMode={isDarkMode} />
        </TabsContent>

        {/* Compensating Controls Tab */}
        <TabsContent value="controls" className="mt-6">
          <CompensatingControlsPanel isDarkMode={isDarkMode} />
        </TabsContent>

        {/* CERT-In Tab */}
        <TabsContent value="certin" className="mt-6">
          <CertInPanel isDarkMode={isDarkMode} />
        </TabsContent>

        <TabsContent value="trends" className="mt-6">
          <div className={`space-y-6 ${isDarkMode ? '' : ''}`}>
            {/* Trend Analytics Header */}
            <div className={`glass rounded-2xl p-6 ${isDarkMode ? '' : 'bg-white/80 border-slate-200'}`}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className={`font-rajdhani font-bold text-2xl ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Compliance Trend Analytics
                  </h2>
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                    Track compliance improvements over time for regulatory audits
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={selectedTrendRegion} onValueChange={setSelectedTrendRegion}>
                    <SelectTrigger className={`w-40 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-200'}`}>
                      <Globe className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Region" />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                      <SelectItem value="all">All Regions</SelectItem>
                      <SelectItem value="india">India Only</SelectItem>
                      <SelectItem value="global">Global Only</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    onClick={fetchTrendAnalytics} 
                    disabled={trendLoading}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {trendLoading ? 'Loading...' : 'Refresh'}
                  </Button>
                </div>
              </div>

              {trendLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className={`font-inter ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>Loading trend analytics...</div>
                </div>
              ) : trendAnalytics ? (
                <div className="space-y-6">
                  {/* Overall Health Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                      <p className={`text-xs font-inter ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Overall Compliance</p>
                      <p className={`text-3xl font-rajdhani font-bold mt-1 ${
                        trendAnalytics.overall_compliance_health >= 70 ? 'text-emerald-400' : 
                        trendAnalytics.overall_compliance_health >= 50 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {trendAnalytics.overall_compliance_health}%
                      </p>
                    </div>
                    <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                      <p className={`text-xs font-inter ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Standards Tracked</p>
                      <p className={`text-3xl font-rajdhani font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {trendAnalytics.total_standards_tracked}
                      </p>
                    </div>
                    <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-orange-500/10 border border-orange-500/30' : 'bg-orange-50 border border-orange-200'}`}>
                      <p className={`text-xs font-inter ${isDarkMode ? 'text-orange-300' : 'text-orange-600'}`}>Indian Compliance Avg</p>
                      <p className="text-3xl font-rajdhani font-bold mt-1 text-orange-400">
                        {trendAnalytics.regional_summary?.india?.avg_score || 0}%
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-orange-300/70' : 'text-orange-500'}`}>
                        {trendAnalytics.regional_summary?.india?.count || 0} standards
                      </p>
                    </div>
                    <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-indigo-500/10 border border-indigo-500/30' : 'bg-indigo-50 border border-indigo-200'}`}>
                      <p className={`text-xs font-inter ${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>Global Compliance Avg</p>
                      <p className="text-3xl font-rajdhani font-bold mt-1 text-indigo-400">
                        {trendAnalytics.regional_summary?.global?.avg_score || 0}%
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-indigo-300/70' : 'text-indigo-500'}`}>
                        {trendAnalytics.regional_summary?.global?.count || 0} standards
                      </p>
                    </div>
                  </div>

                  {/* Trend Direction Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className={`p-4 rounded-xl flex items-center gap-3 ${isDarkMode ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200'}`}>
                      <TrendingUp className="w-8 h-8 text-emerald-400" />
                      <div>
                        <p className="text-2xl font-rajdhani font-bold text-emerald-400">
                          {(trendAnalytics.regional_summary?.india?.improving || 0) + (trendAnalytics.regional_summary?.global?.improving || 0)}
                        </p>
                        <p className={`text-xs ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>Improving</p>
                      </div>
                    </div>
                    <div className={`p-4 rounded-xl flex items-center gap-3 ${isDarkMode ? 'bg-zinc-500/10 border border-zinc-500/30' : 'bg-slate-50 border border-slate-200'}`}>
                      <Minus className={`w-8 h-8 ${isDarkMode ? 'text-zinc-400' : 'text-slate-400'}`} />
                      <div>
                        <p className={`text-2xl font-rajdhani font-bold ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                          {(trendAnalytics.regional_summary?.india?.stable || 0) + (trendAnalytics.regional_summary?.global?.stable || 0)}
                        </p>
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Stable</p>
                      </div>
                    </div>
                    <div className={`p-4 rounded-xl flex items-center gap-3 ${isDarkMode ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200'}`}>
                      <TrendingDown className="w-8 h-8 text-red-400" />
                      <div>
                        <p className="text-2xl font-rajdhani font-bold text-red-400">
                          {(trendAnalytics.regional_summary?.india?.declining || 0) + (trendAnalytics.regional_summary?.global?.declining || 0)}
                        </p>
                        <p className={`text-xs ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>Declining</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={`text-center py-12 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Run some compliance checks to see trend analytics</p>
                </div>
              )}
            </div>

            {/* Individual Standard Trends */}
            {trendAnalytics && trendAnalytics.standards_analytics?.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Indian Compliance Section */}
                {trendAnalytics.indian_compliance?.length > 0 && (
                  <div className={`glass rounded-2xl p-6 ${isDarkMode ? '' : 'bg-white/80 border-slate-200'}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                      <h3 className={`font-rajdhani font-bold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Indian Regulatory Compliance
                      </h3>
                    </div>
                    <div className="space-y-4">
                      {trendAnalytics.indian_compliance.map((std) => (
                        <div key={std.standard_id} className={`p-4 rounded-xl ${isDarkMode ? 'bg-zinc-900/50 border border-white/5' : 'bg-slate-50 border border-slate-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className={`font-rajdhani font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{std.standard_name}</h4>
                              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{std.category}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {std.trend.direction === 'improving' ? (
                                <TrendingUp className="w-5 h-5 text-emerald-400" />
                              ) : std.trend.direction === 'declining' ? (
                                <TrendingDown className="w-5 h-5 text-red-400" />
                              ) : (
                                <Minus className={`w-5 h-5 ${isDarkMode ? 'text-zinc-400' : 'text-slate-400'}`} />
                              )}
                              <span className={`text-2xl font-rajdhani font-bold ${
                                std.statistics.current_score >= 70 ? 'text-emerald-400' : 
                                std.statistics.current_score >= 50 ? 'text-yellow-400' : 'text-red-400'
                              }`}>
                                {std.statistics.current_score}%
                              </span>
                            </div>
                          </div>
                          
                          {/* Mini Trend Chart */}
                          {std.time_series?.length > 1 && (
                            <div className="h-24 mt-2">
                              <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={std.time_series}>
                                  <defs>
                                    <linearGradient id={`gradient-${std.standard_id}`} x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                                      <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                                    </linearGradient>
                                  </defs>
                                  <XAxis dataKey="date" hide />
                                  <YAxis domain={[0, 100]} hide />
                                  <Tooltip 
                                    contentStyle={{ 
                                      backgroundColor: isDarkMode ? '#18181b' : '#fff', 
                                      border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                                      borderRadius: '8px',
                                      color: isDarkMode ? '#fff' : '#1e293b'
                                    }}
                                    formatter={(value) => [`${value}%`, 'Score']}
                                  />
                                  <Area 
                                    type="monotone" 
                                    dataKey="score" 
                                    stroke="#f97316" 
                                    fill={`url(#gradient-${std.standard_id})`}
                                    strokeWidth={2}
                                  />
                                </AreaChart>
                              </ResponsiveContainer>
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                            <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                              {std.statistics.total_checks} checks • {std.trend.description}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${
                              std.statistics.improvement_points > 0 
                                ? 'bg-emerald-500/20 text-emerald-400' 
                                : std.statistics.improvement_points < 0 
                                ? 'bg-red-500/20 text-red-400'
                                : isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-slate-200 text-slate-600'
                            }`}>
                              {std.statistics.improvement_points > 0 ? '+' : ''}{std.statistics.improvement_points} pts
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Global Compliance Section */}
                {trendAnalytics.global_compliance?.length > 0 && (
                  <div className={`glass rounded-2xl p-6 ${isDarkMode ? '' : 'bg-white/80 border-slate-200'}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-3 h-3 rounded-full bg-indigo-500"></div>
                      <h3 className={`font-rajdhani font-bold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Global Compliance Standards
                      </h3>
                    </div>
                    <div className="space-y-4">
                      {trendAnalytics.global_compliance.map((std) => (
                        <div key={std.standard_id} className={`p-4 rounded-xl ${isDarkMode ? 'bg-zinc-900/50 border border-white/5' : 'bg-slate-50 border border-slate-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className={`font-rajdhani font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{std.standard_name}</h4>
                              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{std.category}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {std.trend.direction === 'improving' ? (
                                <TrendingUp className="w-5 h-5 text-emerald-400" />
                              ) : std.trend.direction === 'declining' ? (
                                <TrendingDown className="w-5 h-5 text-red-400" />
                              ) : (
                                <Minus className={`w-5 h-5 ${isDarkMode ? 'text-zinc-400' : 'text-slate-400'}`} />
                              )}
                              <span className={`text-2xl font-rajdhani font-bold ${
                                std.statistics.current_score >= 70 ? 'text-emerald-400' : 
                                std.statistics.current_score >= 50 ? 'text-yellow-400' : 'text-red-400'
                              }`}>
                                {std.statistics.current_score}%
                              </span>
                            </div>
                          </div>
                          
                          {/* Mini Trend Chart */}
                          {std.time_series?.length > 1 && (
                            <div className="h-24 mt-2">
                              <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={std.time_series}>
                                  <defs>
                                    <linearGradient id={`gradient-global-${std.standard_id}`} x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                                    </linearGradient>
                                  </defs>
                                  <XAxis dataKey="date" hide />
                                  <YAxis domain={[0, 100]} hide />
                                  <Tooltip 
                                    contentStyle={{ 
                                      backgroundColor: isDarkMode ? '#18181b' : '#fff', 
                                      border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                                      borderRadius: '8px',
                                      color: isDarkMode ? '#fff' : '#1e293b'
                                    }}
                                    formatter={(value) => [`${value}%`, 'Score']}
                                  />
                                  <Area 
                                    type="monotone" 
                                    dataKey="score" 
                                    stroke="#6366f1" 
                                    fill={`url(#gradient-global-${std.standard_id})`}
                                    strokeWidth={2}
                                  />
                                </AreaChart>
                              </ResponsiveContainer>
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                            <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                              {std.statistics.total_checks} checks • {std.trend.description}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${
                              std.statistics.improvement_points > 0 
                                ? 'bg-emerald-500/20 text-emerald-400' 
                                : std.statistics.improvement_points < 0 
                                ? 'bg-red-500/20 text-red-400'
                                : isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-slate-200 text-slate-600'
                            }`}>
                              {std.statistics.improvement_points > 0 ? '+' : ''}{std.statistics.improvement_points} pts
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        {/* One-Click Exports Tab */}
        <TabsContent value="exports" className="mt-6">
          <div className={`glass rounded-2xl p-6 ${isDarkMode ? '' : 'bg-white/80 border-slate-200'}`}>
            <h3 className="font-rajdhani text-xl text-white mb-4 flex items-center gap-2">
              <Download className="w-5 h-5 text-indigo-400" />
              One-Click Compliance Evidence Exports
            </h3>
            <p className={`mb-6 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
              Generate audit-ready compliance reports and evidence packages instantly for regulatory requirements.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* SOC 2 Type II */}
              <ExportCard
                icon={<Shield className="w-6 h-6 text-emerald-400" />}
                title="SOC 2 Type II Evidence"
                description="Trust service criteria evidence package for auditors"
                endpoint="/compliance/export/soc2"
                filename="soc2_evidence"
                isDarkMode={isDarkMode}
              />

              {/* ISO 27001 SoA */}
              <ExportCard
                icon={<FileText className="w-6 h-6 text-blue-400" />}
                title="ISO 27001 Statement of Applicability"
                description="Annex A control status and justifications"
                endpoint="/compliance/export/iso27001"
                filename="iso27001_soa"
                isDarkMode={isDarkMode}
              />

              {/* PCI-DSS Attestation */}
              <ExportCard
                icon={<CreditCard className="w-6 h-6 text-orange-400" />}
                title="PCI-DSS Attestation"
                description="Quarterly compliance attestation report"
                endpoint="/compliance/export/pci-attestation"
                filename="pci_attestation"
                isDarkMode={isDarkMode}
              />

              {/* DPDP Breach Log */}
              <ExportCard
                icon={<Lock className="w-6 h-6 text-violet-400" />}
                title="DPDP Act 2023 Report"
                description="Personal data protection compliance evidence"
                endpoint="/compliance/export/dpdp"
                filename="dpdp_compliance"
                isDarkMode={isDarkMode}
              />

              {/* CERT-In Report */}
              <ExportCard
                icon={<AlertCircle className="w-6 h-6 text-red-400" />}
                title="CERT-In Incident Report"
                description="6-hour mandatory incident reporting template"
                endpoint="/compliance/export/cert-in"
                filename="certin_incident"
                isDarkMode={isDarkMode}
              />

              {/* NIST POA&M */}
              <ExportCard
                icon={<ClipboardList className="w-6 h-6 text-cyan-400" />}
                title="NIST POA&M"
                description="Plan of Action & Milestones for FedRAMP"
                endpoint="/compliance/export/nist-poam"
                filename="nist_poam"
                isDarkMode={isDarkMode}
              />
            </div>

            {/* Compliance Posture Score */}
            <div className={`mt-8 p-6 rounded-xl ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
              <CompliancePostureScore isDarkMode={isDarkMode} />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Check Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-2xl flex items-center gap-2">
              <FileCheck className="w-6 h-6 text-indigo-400" />
              {selectedCheck?.standard?.toUpperCase()} Compliance Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedCheck && (
            <div className="space-y-4 mt-4">
              {/* Score Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className={`p-4 rounded-lg border ${getScoreBg(selectedCheck.score)}`}>
                  <p className="text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'} mb-1">Overall Score</p>
                  <p className={`text-3xl font-rajdhani font-bold ${getScoreColor(selectedCheck.score)}`}>
                    {selectedCheck.score.toFixed(1)}%
                  </p>
                </div>
                <div className="p-4 bg-emerald-900/20 border border-emerald-500/30 rounded-lg">
                  <p className="text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'} mb-1">Passed Controls</p>
                  <p className="text-3xl font-rajdhani font-bold text-emerald-400">
                    {selectedCheck.passed_controls}
                  </p>
                </div>
                <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
                  <p className="text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'} mb-1">Failed Controls</p>
                  <p className="text-3xl font-rajdhani font-bold text-red-400">
                    {selectedCheck.total_controls - selectedCheck.passed_controls}
                  </p>
                </div>
              </div>
              
              {/* Scan Info */}
              <div className="p-4 bg-zinc-800/50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-zinc-500">Checked At</p>
                    <p className="text-white">{new Date(selectedCheck.checked_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Total Controls</p>
                    <p className="text-white">{selectedCheck.total_controls}</p>
                  </div>
                </div>
              </div>
              
              {/* Findings */}
              {selectedCheck.findings && selectedCheck.findings.length > 0 && (
                <div>
                  <h4 className="text-zinc-300 font-rajdhani font-semibold mb-3">All Findings ({selectedCheck.findings.length})</h4>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {selectedCheck.findings.map((finding, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-lg border ${
                          finding.status === 'passed'
                            ? 'bg-emerald-900/10 border-emerald-500/20'
                            : 'bg-red-900/10 border-red-500/20'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            finding.status === 'passed' ? 'bg-emerald-500/30' : 'bg-red-500/30'
                          }`}>
                            {finding.status === 'passed' ? (
                              <CheckCircle className="w-3 h-3 text-emerald-400" />
                            ) : (
                              <X className="w-3 h-3 text-red-400" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className={`font-medium ${
                              finding.status === 'passed' ? 'text-emerald-300' : 'text-red-300'
                            }`}>
                              {finding.control}
                            </p>
                            {finding.recommendation && (
                              <div className="mt-2 p-2 bg-zinc-900/50 rounded">
                                <p className="text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'} flex items-center gap-1">
                                  <Info className="w-3 h-3" />
                                  Recommendation
                                </p>
                                <p className="text-zinc-300 text-sm mt-1">{finding.recommendation}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Export Card Component for one-click compliance exports
function ExportCard({ icon, title, description, endpoint, filename, isDarkMode }) {
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      const response = await api.get(endpoint);
      const data = response.data;
      
      // Create downloadable JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`${title} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`p-5 rounded-xl border transition-all hover:scale-[1.02] ${
      isDarkMode 
        ? 'bg-zinc-900/50 border-white/10 hover:border-indigo-500/50' 
        : 'bg-white border-slate-200 hover:border-indigo-400'
    }`}>
      <div className="flex items-start gap-3 mb-3">
        {icon}
        <div>
          <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{title}</h4>
          <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{description}</p>
        </div>
      </div>
      <Button 
        onClick={handleExport} 
        disabled={loading}
        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
        data-testid={`export-${filename}-btn`}
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <span className="animate-spin">⏳</span> Generating...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Download className="w-4 h-4" /> Export Report
          </span>
        )}
      </Button>
    </div>
  );
}

// Compliance Posture Score Component
function CompliancePostureScore({ isDarkMode }) {
  const [posture, setPosture] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosture();
  }, []);

  const fetchPosture = async () => {
    try {
      const response = await api.get('/compliance/posture-score');
      setPosture(response.data);
    } catch (error) {
      console.error('Failed to fetch compliance posture:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
        <p className={`mt-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Loading compliance posture...</p>
      </div>
    );
  }

  if (!posture) return null;

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Strong': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      'Moderate': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'Needs Improvement': 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return colors[status] || colors['Needs Improvement'];
  };

  return (
    <div>
      <h4 className={`font-rajdhani text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
        <Shield className="w-5 h-5 text-indigo-400" />
        Real-Time Compliance Posture Score
      </h4>

      <div className="flex items-center gap-6 mb-6">
        <div className="text-center">
          <div className={`text-5xl font-bold ${getScoreColor(posture.overall_posture_score)}`}>
            {posture.overall_posture_score}%
          </div>
          <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs border ${getStatusBadge(posture.compliance_status)}`}>
            {posture.compliance_status}
          </span>
        </div>

        <div className="flex-1 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {Object.entries(posture.standards_breakdown || {}).map(([standard, data]) => (
            <div 
              key={standard} 
              className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-medium uppercase ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                  {standard}
                </span>
                <span className={`text-sm font-bold ${getScoreColor(data.score)}`}>
                  {data.score}%
                </span>
              </div>
              <Progress value={data.score} className="h-1.5" />
              <div className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                {data.passed}/{data.total} passed
              </div>
            </div>
          ))}
        </div>
      </div>

      {posture.recommendations?.length > 0 && (
        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
          <p className={`text-sm flex items-center gap-2 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
            <AlertTriangle className="w-4 h-4" />
            <strong>Recommendation:</strong> {posture.recommendations[0]}
          </p>
        </div>
      )}
    </div>
  );
}

// Exception Approval Workflows Panel
function ExceptionWorkflowPanel({ isDarkMode }) {
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newException, setNewException] = useState({
    compliance_framework: 'soc2',
    exception_type: 'risk_acceptance',
    justification: '',
    business_reason: '',
    compensating_controls: [],
    requested_duration_days: 90
  });

  useEffect(() => {
    fetchExceptions();
  }, []);

  const fetchExceptions = async () => {
    try {
      const response = await api.get('/compliance/exceptions');
      setExceptions(response.data.exceptions || []);
    } catch (error) {
      console.error('Failed to fetch exceptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const createException = async () => {
    try {
      await api.post('/compliance/exceptions', newException);
      toast.success('Exception request created');
      setShowCreateModal(false);
      fetchExceptions();
    } catch (error) {
      toast.error('Failed to create exception');
    }
  };

  const approveException = async (exceptionId, approved, comments = '') => {
    try {
      await api.post(`/compliance/exceptions/${exceptionId}/approve`, { approved, comments });
      toast.success(approved ? 'Exception approved' : 'Exception rejected');
      fetchExceptions();
    } catch (error) {
      toast.error('Failed to process approval');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending_approval: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      approved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
      expired: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
      revoked: 'bg-orange-500/20 text-orange-400 border-orange-500/30'
    };
    return colors[status] || colors.pending_approval;
  };

  if (loading) {
    return <div className="text-center py-8"><div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div></div>;
  }

  return (
    <div className={`glass rounded-2xl p-6 ${isDarkMode ? '' : 'bg-white/80 border-slate-200'}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className={`font-rajdhani text-xl ${isDarkMode ? 'text-white' : 'text-slate-900'} flex items-center gap-2`}>
            <Shield className="w-5 h-5 text-indigo-400" />
            Exception Approval Workflows
          </h3>
          <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
            Manage risk acceptance and compliance exceptions with approval chains
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
          <Shield className="w-4 h-4 mr-2" /> Request Exception
        </Button>
      </div>

      {exceptions.length === 0 ? (
        <div className={`text-center py-12 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
          <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No exception requests yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {exceptions.map((exc) => (
            <div key={exc.id} className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/50 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${getStatusBadge(exc.status)}`}>
                      {exc.status.replace('_', ' ')}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                      {exc.compliance_framework.toUpperCase()}
                    </span>
                    <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                      {exc.exception_type.replace('_', ' ')}
                    </span>
                  </div>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{exc.justification}</p>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{exc.business_reason}</p>
                  {exc.compensating_controls?.length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {exc.compensating_controls.map((ctrl, i) => (
                        <span key={i} className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-cyan-500/20 text-cyan-300' : 'bg-cyan-100 text-cyan-700'}`}>
                          {ctrl}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-4 mt-3 text-xs">
                    <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-400'}>
                      Requested: {new Date(exc.created_at).toLocaleDateString()}
                    </span>
                    <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-400'}>
                      Duration: {exc.requested_duration_days} days
                    </span>
                    <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-400'}>
                      Approval: Step {(exc.current_approval_step || 0) + 1}/{exc.approval_chain?.length || 0}
                    </span>
                  </div>
                </div>
                {exc.status === 'pending_approval' && (
                  <div className="flex gap-2 ml-4">
                    <Button size="sm" variant="outline" onClick={() => approveException(exc.id, false, 'Rejected')} className="text-red-500 border-red-500/30">
                      Reject
                    </Button>
                    <Button size="sm" onClick={() => approveException(exc.id, true, 'Approved')} className="bg-emerald-600 hover:bg-emerald-700">
                      Approve
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Exception Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
          <DialogHeader>
            <DialogTitle>Request Compliance Exception</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Framework</label>
                <Select value={newException.compliance_framework} onValueChange={(v) => setNewException({...newException, compliance_framework: v})}>
                  <SelectTrigger className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                    <SelectItem value="soc2">SOC 2</SelectItem>
                    <SelectItem value="iso-27001">ISO 27001</SelectItem>
                    <SelectItem value="pci-dss">PCI-DSS</SelectItem>
                    <SelectItem value="dpdp">DPDP</SelectItem>
                    <SelectItem value="hipaa">HIPAA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Exception Type</label>
                <Select value={newException.exception_type} onValueChange={(v) => setNewException({...newException, exception_type: v})}>
                  <SelectTrigger className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                    <SelectItem value="risk_acceptance">Risk Acceptance</SelectItem>
                    <SelectItem value="compensating_control">Compensating Control</SelectItem>
                    <SelectItem value="temporary_exception">Temporary Exception</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Justification</label>
              <Input
                value={newException.justification}
                onChange={(e) => setNewException({...newException, justification: e.target.value})}
                placeholder="Why is this exception needed?"
                className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Business Reason</label>
              <Input
                value={newException.business_reason}
                onChange={(e) => setNewException({...newException, business_reason: e.target.value})}
                placeholder="Business impact of not having this exception"
                className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Duration (days)</label>
              <Input
                type="number"
                value={newException.requested_duration_days}
                onChange={(e) => setNewException({...newException, requested_duration_days: parseInt(e.target.value)})}
                className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
              />
            </div>
            <Button onClick={createException} className="w-full bg-indigo-600 hover:bg-indigo-700">
              Submit Exception Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Compensating Controls Panel
function CompensatingControlsPanel({ isDarkMode }) {
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newControl, setNewControl] = useState({
    name: '',
    description: '',
    control_type: 'technical',
    mitigates_risk: '',
    verification_frequency: 'monthly',
    verification_method: 'manual_review'
  });

  useEffect(() => {
    fetchControls();
  }, []);

  const fetchControls = async () => {
    try {
      const response = await api.get('/compliance/compensating-controls');
      setControls(response.data.controls || []);
    } catch (error) {
      console.error('Failed to fetch controls:', error);
    } finally {
      setLoading(false);
    }
  };

  const createControl = async () => {
    try {
      await api.post('/compliance/compensating-controls', newControl);
      toast.success('Compensating control created');
      setShowCreateModal(false);
      fetchControls();
    } catch (error) {
      toast.error('Failed to create control');
    }
  };

  const verifyControl = async (controlId) => {
    try {
      await api.post(`/compliance/compensating-controls/${controlId}/verify`, {
        method: 'manual_review',
        result: 'passed',
        notes: 'Verification completed successfully'
      });
      toast.success('Control verified');
      fetchControls();
    } catch (error) {
      toast.error('Failed to verify control');
    }
  };

  if (loading) {
    return <div className="text-center py-8"><div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div></div>;
  }

  return (
    <div className={`glass rounded-2xl p-6 ${isDarkMode ? '' : 'bg-white/80 border-slate-200'}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className={`font-rajdhani text-xl ${isDarkMode ? 'text-white' : 'text-slate-900'} flex items-center gap-2`}>
            <Lock className="w-5 h-5 text-cyan-400" />
            Compensating Controls
          </h3>
          <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
            Track alternative controls and verification schedules
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-cyan-600 hover:bg-cyan-700">
          <Lock className="w-4 h-4 mr-2" /> Add Control
        </Button>
      </div>

      {controls.length === 0 ? (
        <div className={`text-center py-12 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
          <Lock className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No compensating controls registered</p>
        </div>
      ) : (
        <div className="space-y-4">
          {controls.map((ctrl) => (
            <div key={ctrl.id} className={`p-4 rounded-xl border ${ctrl.verification_overdue ? 'border-red-500/50' : ''} ${isDarkMode ? 'bg-zinc-900/50 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${ctrl.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                      {ctrl.status}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-cyan-500/20 text-cyan-300' : 'bg-cyan-100 text-cyan-700'}`}>
                      {ctrl.control_type}
                    </span>
                    {ctrl.verification_overdue && (
                      <span className="px-2 py-1 rounded text-xs bg-red-500/20 text-red-400 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Verification Overdue
                      </span>
                    )}
                  </div>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{ctrl.name}</p>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{ctrl.description}</p>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                    <strong>Mitigates:</strong> {ctrl.mitigates_risk}
                  </p>
                  <div className="flex items-center gap-4 mt-3 text-xs">
                    <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-400'}>
                      Verification: {ctrl.verification_frequency}
                    </span>
                    <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-400'}>
                      Next due: {ctrl.next_verification_due ? new Date(ctrl.next_verification_due).toLocaleDateString() : 'Not set'}
                    </span>
                    <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-400'}>
                      Method: {ctrl.verification_method.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                <Button size="sm" onClick={() => verifyControl(ctrl.id)} className="bg-emerald-600 hover:bg-emerald-700 ml-4">
                  <CheckCircle className="w-4 h-4 mr-1" /> Verify
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Control Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
          <DialogHeader>
            <DialogTitle>Register Compensating Control</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Control Name</label>
              <Input
                value={newControl.name}
                onChange={(e) => setNewControl({...newControl, name: e.target.value})}
                placeholder="e.g., Enhanced Network Segmentation"
                className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input
                value={newControl.description}
                onChange={(e) => setNewControl({...newControl, description: e.target.value})}
                placeholder="Describe the control implementation"
                className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Risk Mitigated</label>
              <Input
                value={newControl.mitigates_risk}
                onChange={(e) => setNewControl({...newControl, mitigates_risk: e.target.value})}
                placeholder="What risk does this control address?"
                className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Control Type</label>
                <Select value={newControl.control_type} onValueChange={(v) => setNewControl({...newControl, control_type: v})}>
                  <SelectTrigger className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="administrative">Administrative</SelectItem>
                    <SelectItem value="physical">Physical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Verification Frequency</label>
                <Select value={newControl.verification_frequency} onValueChange={(v) => setNewControl({...newControl, verification_frequency: v})}>
                  <SelectTrigger className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annually">Annually</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={createControl} className="w-full bg-cyan-600 hover:bg-cyan-700">
              Register Control
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// CERT-In Incident Panel
function CertInPanel({ isDarkMode }) {
  const [config, setConfig] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [newIncident, setNewIncident] = useState({
    category: 'data_breach',
    description: '',
    affected_systems: [],
    estimated_impact: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [configRes, incidentsRes] = await Promise.all([
        api.get('/compliance/cert-in/config'),
        api.get('/compliance/cert-in/incidents')
      ]);
      setConfig(configRes.data);
      setIncidents(incidentsRes.data.incidents || []);
    } catch (error) {
      console.error('Failed to fetch CERT-In data:', error);
    } finally {
      setLoading(false);
    }
  };

  const triggerIncident = async () => {
    try {
      const response = await api.post('/compliance/cert-in/trigger', newIncident);
      toast.success(`CERT-In incident triggered. Deadline: ${response.data.deadline_hours_remaining} hours`);
      setShowTriggerModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to trigger incident');
    }
  };

  const getDeadlineColor = (hours) => {
    if (hours <= 1) return 'text-red-400';
    if (hours <= 3) return 'text-orange-400';
    return 'text-yellow-400';
  };

  if (loading) {
    return <div className="text-center py-8"><div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div></div>;
  }

  return (
    <div className={`glass rounded-2xl p-6 ${isDarkMode ? '' : 'bg-white/80 border-slate-200'}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className={`font-rajdhani text-xl ${isDarkMode ? 'text-white' : 'text-slate-900'} flex items-center gap-2`}>
            <AlertCircle className="w-5 h-5 text-red-400" />
            CERT-In 6-Hour Incident Reporting
          </h3>
          <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
            Automated compliance with mandatory incident reporting requirements
          </p>
        </div>
        <div className="flex gap-2">
          <span className={`px-3 py-1 rounded-full text-sm ${config?.enabled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
            {config?.enabled ? 'Auto-Detection Enabled' : 'Not Configured'}
          </span>
          <Button onClick={() => setShowTriggerModal(true)} className="bg-red-600 hover:bg-red-700">
            <AlertCircle className="w-4 h-4 mr-2" /> Trigger Incident
          </Button>
        </div>
      </div>

      {/* Active Incidents Alert */}
      {incidents.filter(i => i.status !== 'submitted' && i.status !== 'closed').length > 0 && (
        <div className={`p-4 rounded-xl mb-6 ${isDarkMode ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-400 animate-pulse" />
            <div>
              <p className={`font-semibold ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                {incidents.filter(i => i.status !== 'submitted' && i.status !== 'closed').length} Active Incident(s) Requiring Attention
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-red-400/80' : 'text-red-600'}`}>
                CERT-In requires reporting within 6 hours of detection
              </p>
            </div>
          </div>
        </div>
      )}

      {incidents.length === 0 ? (
        <div className={`text-center py-12 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
          <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No CERT-In incidents recorded</p>
        </div>
      ) : (
        <div className="space-y-4">
          {incidents.map((incident) => (
            <div key={incident.id} className={`p-4 rounded-xl border ${incident.is_overdue ? 'border-red-500' : ''} ${isDarkMode ? 'bg-zinc-900/50 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${
                      incident.status === 'submitted' ? 'bg-emerald-500/20 text-emerald-400' :
                      incident.is_overdue ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {incident.status}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700'}`}>
                      {incident.incident_category?.replace('_', ' ')}
                    </span>
                    {incident.auto_detected && (
                      <span className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                        Auto-Detected
                      </span>
                    )}
                  </div>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{incident.incident_description || 'Security Incident'}</p>
                  {incident.affected_systems?.length > 0 && (
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                      <strong>Affected:</strong> {incident.affected_systems.join(', ')}
                    </p>
                  )}
                  <div className="flex items-center gap-4 mt-3 text-xs">
                    <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-400'}>
                      Triggered: {new Date(incident.triggered_at).toLocaleString()}
                    </span>
                    {incident.status !== 'submitted' && (
                      <span className={`font-semibold ${getDeadlineColor(incident.deadline_hours_remaining)}`}>
                        ⏱ {incident.deadline_hours_remaining?.toFixed(1)}h remaining
                      </span>
                    )}
                    {incident.submitted_at && (
                      <span className="text-emerald-400">
                        ✓ Submitted: {new Date(incident.submitted_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {/* Workflow Progress */}
              {incident.workflow_steps && (
                <div className="mt-4 flex gap-2 flex-wrap">
                  {incident.workflow_steps.map((step, i) => (
                    <span key={i} className={`px-2 py-1 rounded text-xs ${
                      step.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                      step.status === 'pending' ? 'bg-zinc-500/20 text-zinc-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {step.step.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Trigger Incident Modal */}
      <Dialog open={showTriggerModal} onOpenChange={setShowTriggerModal}>
        <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5" /> Trigger CERT-In Incident Report
            </DialogTitle>
          </DialogHeader>
          <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
              <strong>Warning:</strong> This will initiate a 6-hour mandatory reporting workflow. Ensure this is a genuine security incident.
            </p>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Incident Category</label>
              <Select value={newIncident.category} onValueChange={(v) => setNewIncident({...newIncident, category: v})}>
                <SelectTrigger className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                  <SelectItem value="data_breach">Data Breach</SelectItem>
                  <SelectItem value="ransomware">Ransomware Attack</SelectItem>
                  <SelectItem value="unauthorized_access">Unauthorized Access</SelectItem>
                  <SelectItem value="ddos">DDoS Attack</SelectItem>
                  <SelectItem value="malware">Malware Infection</SelectItem>
                  <SelectItem value="phishing">Phishing Attack</SelectItem>
                  <SelectItem value="defacement">Website Defacement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input
                value={newIncident.description}
                onChange={(e) => setNewIncident({...newIncident, description: e.target.value})}
                placeholder="Brief description of the incident"
                className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Estimated Impact</label>
              <Input
                value={newIncident.estimated_impact}
                onChange={(e) => setNewIncident({...newIncident, estimated_impact: e.target.value})}
                placeholder="e.g., ~10,000 customer records potentially affected"
                className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
              />
            </div>
            <Button onClick={triggerIncident} className="w-full bg-red-600 hover:bg-red-700">
              <AlertCircle className="w-4 h-4 mr-2" /> Initiate 6-Hour Reporting Workflow
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
